import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Plug, Search, CheckCircle2, AlertCircle, Clock, PlusCircle } from "lucide-react";

const STATUS = {
  ready: { label: "Ready to sync", cls: "bg-brand-green text-brand-cream", icon: CheckCircle2 },
  waiting: { label: "Waiting on Awin", cls: "bg-brand-amber/20 text-brand-ink border border-brand-amber/40", icon: Clock },
  feed_unknown: { label: "Check availability", cls: "bg-brand-amber/20 text-brand-ink border border-brand-amber/40", icon: Search },
  blocked_currency: { label: "Wrong currency", cls: "bg-brand-burgundy/10 text-brand-burgundy border border-brand-burgundy/30", icon: AlertCircle },
  no_feed: { label: "No product feed", cls: "bg-brand-cream2 text-brand-ink/70", icon: AlertCircle },
  not_joined: { label: "Not approved", cls: "bg-brand-cream2 text-brand-ink/50", icon: AlertCircle },
};

const fmtTime = (v) => (v ? new Date(v).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "—");

export const RetailerPicker = ({ open, onOpenChange, connected, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [approvedOnly, setApprovedOnly] = useState(true);
  const [feedOnly, setFeedOnly] = useState(false);
  const [busy, setBusy] = useState("");
  const [manual, setManual] = useState({ advertiser_id: "", feed_url: "" });
  const [check, setCheck] = useState(null);
  const [probes, setProbes] = useState({}); // advertiser_id -> validate result / error

  const probe = async (row) => {
    setBusy(`probe-${row.advertiser_id}`);
    try {
      const r = await api.post("/admin/awin/validate", {
        advertiser_id: String(row.advertiser_id), advertiser_name: row.advertiser_name,
      });
      setProbes((p) => ({ ...p, [row.advertiser_id]: r.data }));
    } catch (e) {
      setProbes((p) => ({ ...p, [row.advertiser_id]: { error: e.response?.data?.detail || "No feed found" } }));
    } finally { setBusy(""); }
  };

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/awin/advertisers", { params: refresh ? { refresh: true } : {} });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reach Awin");
      setData({ programmes: [], warnings: [e.response?.data?.detail || "Awin unavailable"] });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && !data) load(); }, [open]); // eslint-disable-line

  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (data?.programmes || []).filter((r) => {
      if (approvedOnly && !r.joined) return false;
      if (feedOnly && !r.feeds.length) return false;
      if (ql && !(`${r.advertiser_name} ${r.advertiser_id}`.toLowerCase().includes(ql))) return false;
      return true;
    }).slice(0, 200);
  }, [data, q, approvedOnly, feedOnly]);

  const connect = async (row, feed) => {
    setBusy(`c-${row.advertiser_id}-${feed?.feed_id || "x"}`);
    try {
      await api.post("/admin/retailers", {
        advertiser_id: String(row.advertiser_id),
        advertiser_name: row.advertiser_name,
        enabled: true, min_price: 10, max_price: 500, cap: 300,
        feed_categories: [], feed_url: feed?.feed_url || null, feed_id: feed?.feed_id || null,
      });
      toast.success(`${row.advertiser_name} connected`);
      onChanged?.();
      load();
    } catch { toast.error("Could not connect retailer"); }
    finally { setBusy(""); }
  };

  const validate = async () => {
    setBusy("validate");
    setCheck(null);
    try {
      const r = await api.post("/admin/awin/validate", {
        advertiser_id: manual.advertiser_id.trim() || null,
        feed_url: manual.feed_url.trim() || null,
      });
      setCheck(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read that feed"); }
    finally { setBusy(""); }
  };

  const saveManual = async () => {
    setBusy("manual-save");
    try {
      await api.post("/admin/retailers", {
        advertiser_id: String(check.advertiser_id),
        advertiser_name: check.advertiser_name,
        enabled: true, min_price: 10, max_price: 500, cap: 300,
        feed_categories: [], feed_url: check.feed_url || null,
      });
      toast.success(`${check.advertiser_name} added`);
      setCheck(null);
      setManual({ advertiser_id: "", feed_url: "" });
      onChanged?.();
      load();
    } catch { toast.error("Could not add retailer"); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="retailer-picker">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Find retailers</DialogTitle>
          <DialogDescription>
            Programmes this Awin account is approved for, and whether each one publishes a product feed.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"
              placeholder="Search retailer or advertiser ID…" data-testid="picker-search" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={approvedOnly} onCheckedChange={setApprovedOnly} data-testid="picker-approved-filter" />
            Approved only
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={feedOnly} onCheckedChange={setFeedOnly} data-testid="picker-feed-filter" />
            Has a feed
          </label>
          <Button variant="outline" size="sm" className="rounded-full gap-2" onClick={() => load(true)}
            disabled={loading} data-testid="picker-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </Button>
        </div>

        {data && (
          <p className="text-xs text-muted-foreground" data-testid="picker-meta">
            {data.joined_count ?? 0} approved · {data.ready_count ?? 0} ready to sync · read from Awin at{" "}
            {fmtTime(data.fetched_at)}{data.cached ? " (cached)" : ""}
          </p>
        )}
        {data?.warnings?.map((w) => (
          <p key={w} className="text-xs text-brand-burgundy" data-testid="picker-warning">{w}</p>
        ))}

        {loading && !data ? <Loader2 className="w-5 h-5 animate-spin" /> : (
          <div className="space-y-3">
            {rows.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="picker-empty">
                Nothing matches those filters.
              </p>
            )}
            {rows.map((row) => {
              const s = STATUS[row.status] || STATUS.no_feed;
              const Icon = s.icon;
              return (
                <div key={row.advertiser_id} data-testid={`picker-row-${row.advertiser_id}`}
                  className="border border-brand-cream2 rounded-xl p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-medium">{row.advertiser_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        #{row.advertiser_id}{row.region ? ` · ${row.region}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {row.connected && (
                        <Badge className="bg-brand-cream2 text-brand-ink/70" data-testid={`picker-connected-${row.advertiser_id}`}>
                          Connected
                        </Badge>
                      )}
                      <Badge className={`${s.cls} gap-1`} data-testid={`picker-status-${row.advertiser_id}`}>
                        <Icon className="w-3 h-3" /> {s.label}
                      </Badge>
                    </div>
                  </div>
                  {row.reason && (
                    <p className="text-xs text-muted-foreground mt-2" data-testid={`picker-reason-${row.advertiser_id}`}>
                      {row.reason}
                    </p>
                  )}
                  {row.checkable && (
                    <div className="mt-3 flex flex-wrap items-center gap-3" data-testid={`picker-probe-${row.advertiser_id}`}>
                      <Button variant="outline" size="sm" className="rounded-full gap-2"
                        onClick={() => probe(row)} disabled={busy === `probe-${row.advertiser_id}`}
                        data-testid={`picker-check-${row.advertiser_id}`}>
                        {busy === `probe-${row.advertiser_id}`
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Search className="w-3.5 h-3.5" />} Check feed
                      </Button>
                      {probes[row.advertiser_id]?.error && (
                        <span className="text-xs text-brand-burgundy">{probes[row.advertiser_id].error}</span>
                      )}
                      {probes[row.advertiser_id]?.importable && (
                        <>
                          <span className="text-xs text-muted-foreground">
                            {probes[row.advertiser_id].usable_products} GBP products · £
                            {probes[row.advertiser_id].min_price}–£{probes[row.advertiser_id].max_price}
                          </span>
                          <Button size="sm" className="bg-brand-green hover:bg-brand-greendark rounded-full gap-2"
                            disabled={row.connected || busy === `c-${row.advertiser_id}-x`}
                            data-testid={`picker-connect-enhanced-${row.advertiser_id}`}
                            onClick={() => connect(row, null)}>
                            <Plug className="w-3.5 h-3.5" /> {row.connected ? "Connected" : "Connect"}
                          </Button>
                        </>
                      )}
                      {probes[row.advertiser_id] && !probes[row.advertiser_id].importable && !probes[row.advertiser_id].error && (
                        <span className="text-xs text-brand-burgundy">{probes[row.advertiser_id].reason}</span>
                      )}
                    </div>
                  )}
                  {row.feeds.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {row.feeds.map((f) => {
                        const inUse = row.connected && String(row.in_use_feed_id) === String(f.feed_id);
                        const gbp = (f.currency || "GBP").toUpperCase() === "GBP";
                        return (
                          <div key={f.feed_id} data-testid={`picker-feed-${f.feed_id}`}
                            className="flex flex-wrap items-center justify-between gap-3 bg-brand-cream2/40 rounded-lg px-3 py-2">
                            <div className="min-w-0">
                              <p className="text-sm truncate">{f.feed_name}</p>
                              <p className="text-xs text-muted-foreground font-mono">
                                feed {f.feed_id} · {f.no_of_products || "?"} products · {(f.currency || "GBP").toUpperCase()}
                                {f.language ? ` · ${f.language}` : ""}
                              </p>
                            </div>
                            {gbp ? (
                              <Button size="sm" className="bg-brand-green hover:bg-brand-greendark rounded-full gap-2"
                                disabled={inUse || busy === `c-${row.advertiser_id}-${f.feed_id}`}
                                data-testid={`picker-connect-${row.advertiser_id}-${f.feed_id}`}
                                onClick={() => connect(row, f)}>
                                <Plug className="w-3.5 h-3.5" />
                                {inUse ? "In use" : row.connected ? "Use this feed" : "Connect"}
                              </Button>
                            ) : (
                              <span className="text-xs text-brand-burgundy">
                                {(f.currency || "").toUpperCase()} feed — GBP only
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="border-t border-brand-cream2 pt-4 mt-2" data-testid="picker-manual">
          <p className="font-serif text-lg flex items-center gap-2"><PlusCircle className="w-4 h-4 text-brand-amber" /> Add manually</p>
          <p className="text-xs text-muted-foreground mt-1">
            Approved in Awin but missing above? Paste the advertiser ID or the Create-a-Feed download URL —
            we'll download it and tell you what's in it before saving anything.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <div>
              <Label className="text-xs">Advertiser ID</Label>
              <Input value={manual.advertiser_id} data-testid="manual-advertiser-id"
                onChange={(e) => setManual({ ...manual, advertiser_id: e.target.value })} placeholder="129701" />
            </div>
            <div>
              <Label className="text-xs">Create-a-Feed URL (optional)</Label>
              <Input value={manual.feed_url} data-testid="manual-feed-url"
                onChange={(e) => setManual({ ...manual, feed_url: e.target.value })} placeholder="https://productdata.awin.com/datafeed/download/…" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <Button variant="outline" className="rounded-full gap-2" onClick={validate}
              disabled={busy === "validate" || (!manual.advertiser_id.trim() && !manual.feed_url.trim())}
              data-testid="manual-validate">
              {busy === "validate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Check feed
            </Button>
            {check && (
              <>
                <p className="text-sm" data-testid="manual-result">
                  <span className="font-medium">{check.advertiser_name}</span> · {check.feed_rows} rows ·{" "}
                  {check.usable_products} usable · {check.currencies.join("/") || "?"}
                  {check.min_price != null ? ` · £${check.min_price}–£${check.max_price}` : ""}
                  {check.reason ? ` · ${check.reason}` : ""}
                </p>
                {check.importable && (
                  <Button className="bg-brand-green hover:bg-brand-greendark rounded-full gap-2"
                    onClick={saveManual} disabled={busy === "manual-save"} data-testid="manual-save">
                    <Plug className="w-4 h-4" /> Add {check.advertiser_name}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
